import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../lib/api';
import { useNotifications } from '../../hooks/useNotifications';

const COUNTRIES = [
  'United States', 'Canada', 'United Kingdom', 'Australia',
  'Germany', 'France', 'Italy', 'Spain', 'Netherlands',
  'Belgium', 'Switzerland', 'Austria', 'Sweden', 'Norway',
  'Denmark', 'Finland', 'Ireland', 'New Zealand', 'Japan',
  'South Korea', 'Singapore', 'India', 'Brazil', 'Mexico',
  'Argentina', 'Chile', 'Other'
];

export default function EditProfile() {
  const { user, login, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSuccess, showError } = useNotifications();

  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [location, setLocation] = useState('');
  const [userType, setUserType] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  useEffect(() => {
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    try {
      // Try to get user info from API first
      let data;
      try {
        data = await apiService.getUserInfo();
      } catch (apiError) {
        console.warn('API getUserInfo failed, using auth user:', apiError);
        // Fallback to user from auth store
        data = user;
      }

      setUsername(data?.username || user?.name || user?.email?.split('@')[0] || '');
      setFullName(data?.fullName || data?.name || user?.name || '');
      setLocation(data?.country || 'United States');
      setUserType(data?.userType || user?.userType || 'surgeon');
      setProfilePicture(data?.profilePicture || data?.avatar || user?.avatar || '');
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError('Invalid File', 'Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showError('File Too Large', 'Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('media', file);

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3009'}/api/v1/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const { url } = await response.json();
      setProfilePicture(url);
      showSuccess('Image Uploaded', 'Profile picture uploaded successfully');
    } catch (error) {
      console.error('Failed to upload image:', error);
      showError('Upload Failed', 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const payload: any = {
        username,
        fullName,
        country: location,
      };

      if (profilePicture) {
        payload.profilePicture = profilePicture;
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3009'}/api/v1/user/update_profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || errorData.message || 'Update failed');
      }

      // Update user in auth store
      if (token && user) {
        const nameParts = (fullName || '').split(' ');
        login(token, {
          ...user,
          username,
          name: fullName,
          fullName,
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          country: location,
          avatar: profilePicture,
        });
      }

      showSuccess('Profile Updated', 'Your profile has been updated successfully');
      navigate('/profile');
    } catch (error) {
      console.error('Error updating profile:', error);
      showError('Update Failed', 'Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getUserInitials = (name: string) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (isLoadingProfile) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-healthy-green"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold theme-text-primary">Edit Profile</h1>
        <button
          onClick={() => navigate('/profile')}
          className="theme-text-secondary hover:theme-text-primary p-2 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Avatar Section */}
      <div className="theme-bg-primary rounded-lg shadow-sm border theme-border-primary p-8 mb-6">
        <div className="flex flex-col items-center">
          <div className="relative mb-4">
            {profilePicture ? (
              <img
                src={profilePicture}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-healthy-green to-primary-600 flex items-center justify-center text-white text-2xl font-bold">
                {getUserInitials(fullName || user?.email || '')}
              </div>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="absolute bottom-0 right-0 bg-healthy-green text-white p-2 rounded-full shadow-lg hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              {isUploading ? (
                <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
          </div>
          <p className="text-sm theme-text-secondary">Tap to change profile picture</p>
        </div>
      </div>

      {/* Form Fields */}
      <div className="theme-bg-primary rounded-lg shadow-sm border theme-border-primary p-6 mb-6">
        <div className="space-y-6">
          {/* Username */}
          <div>
            <label className="block text-xs font-semibold theme-text-secondary uppercase tracking-wider mb-2">
              USERNAME
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 theme-bg-secondary theme-text-primary border theme-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-healthy-green"
              placeholder="Enter username"
              maxLength={30}
            />
            <p className="text-xs theme-text-secondary mt-1">{username.length}/30 characters</p>
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-xs font-semibold theme-text-secondary uppercase tracking-wider mb-2">
              FULL NAME (NOT VISIBLE TO OTHER USERS)
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-3 theme-bg-secondary theme-text-primary border theme-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-healthy-green"
              placeholder="Enter full name"
              maxLength={50}
            />
            <p className="text-xs theme-text-secondary mt-1">{fullName.length}/50 characters</p>
          </div>

          {/* Occupation */}
          <div>
            <label className="block text-xs font-semibold theme-text-secondary uppercase tracking-wider mb-2">
              OCCUPATION
            </label>
            <div className="px-4 py-3 theme-bg-secondary theme-text-primary border theme-border-primary rounded-lg">
              {userType ? userType.charAt(0).toUpperCase() + userType.slice(1) : 'Loading...'}
            </div>
          </div>

          {/* Country */}
          <div>
            <label className="block text-xs font-semibold theme-text-secondary uppercase tracking-wider mb-2">
              COUNTRY
            </label>
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full px-4 py-3 theme-bg-secondary theme-text-primary border theme-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-healthy-green appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1.25rem',
              }}
            >
              {COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          {/* Email (locked) */}
          <div>
            <label className="block text-xs font-semibold theme-text-secondary uppercase tracking-wider mb-2">
              EMAIL
            </label>
            <div className="flex items-center px-4 py-3 theme-bg-secondary border theme-border-primary rounded-lg">
              <span className="theme-text-secondary flex-1">{user?.email}</span>
              <svg className="w-5 h-5 theme-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p className="text-xs theme-text-secondary mt-1">Email cannot be changed</p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={isSaving}
        className="w-full bg-healthy-green text-white py-3 rounded-lg hover:bg-green-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? 'Saving...' : 'Submit'}
      </button>
    </div>
  );
}
